import React, { useState } from "react";
import {
    Grid,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Typography,
    Paper,
    Alert,
    Tooltip,
} from "@mui/material";
import {
    Science as ScienceIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../common/CommonLoader";
import { ReserveLabOrders } from "../../services/OrderService";
import { appInsights } from "../../../services/appInisights";

// Lab configuration - can be expanded in the future
const AVAILABLE_LABS = [
    { labName: "Madison Lab", accountNumber: "7003", labId: "Madison", defaultCount: 1200 },
    // Add more labs here in the future
    // { labName: "Another Lab", accountNumber: "XXXX", labId: "LabId", defaultCount: 500 },
];

export default function ReserveOrders() {
    const [loading, setLoading] = useState(false);
    
    // Form data
    const [selectedLab, setSelectedLab] = useState("");
    const [ordersCount, setOrdersCount] = useState(1200);
    const [errors, setErrors] = useState({});

    // Get selected lab details
    const getSelectedLabDetails = () => {
        return AVAILABLE_LABS.find(lab => lab.labId === selectedLab);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        if (!selectedLab) {
            newErrors.lab = "Lab selection is required";
            isValid = false;
        }

        if (!ordersCount || ordersCount < 1) {
            newErrors.ordersCount = "Count must be at least 1";
            isValid = false;
        }

        if (ordersCount > 10000) {
            newErrors.ordersCount = "Count cannot exceed 10,000";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            NotificationManager.error("Please fill in all required fields");
            return;
        }

        const labDetails = getSelectedLabDetails();
        if (!labDetails) {
            NotificationManager.error("Invalid lab selection");
            return;
        }

        try {
            setLoading(true);

            // Prepare data for API
            const reserveOrdersData = {
                labName: labDetails.labName,
                accountNumber: labDetails.accountNumber,
                labId: labDetails.labId,
                ordersCount: ordersCount,
            };

            // Call the API
            await ReserveLabOrders(reserveOrdersData);

            NotificationManager.success(
                `Successfully reserved ${ordersCount} orders for ${labDetails.labName}`,
                "Success"
            );

            // Reset form
            handleReset();

        } catch (error) {
            appInsights.trackEvent({
                name: "Error Reserving Lab Orders",
                properties: {
                    message: "Error reserving lab orders from ReserveOrders.js",
                    error: error,
                    lab: labDetails?.labName,
                    count: ordersCount,
                },
            });
            appInsights.trackException({ exception: error });
            NotificationManager.error(
                "Unable to reserve orders. Error: " + error,
                "Error Submitting"
            );
        } finally {
            setLoading(false);
        }
    };

    // Reset form
    const handleReset = () => {
        setSelectedLab("");
        setOrdersCount(1200);
        setErrors({});
    };

    // Handle lab selection change
    const handleLabChange = (labId) => {
        setSelectedLab(labId);
        const lab = AVAILABLE_LABS.find(l => l.labId === labId);
        if (lab) {
            setOrdersCount(lab.defaultCount);
        }
        setErrors((prev) => ({ ...prev, lab: "" }));
    };

    // Check if order count is large (>500)
    const isLargeOrder = ordersCount > 500;

    return (
        <>
            <div className="vlp-component-container" style={{ marginRight: "15px" }}>
                <Box sx={{ marginBottom: 3 }}>
                    <Typography variant="h5" className="title" sx={{ fontWeight: 600, mb: 1 }}>
                        Reserve Lab Orders
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Reserve a specific number of orders for laboratory processing
                    </Typography>
                </Box>

                <hr style={{ marginBottom: "24px" }} />

                {/* Main Form Card */}
                <Paper elevation={2} sx={{ padding: 3, marginBottom: 3, borderRadius: 2 }}>
                    <Grid container spacing={3}>
                        {/* Lab Selection */}
                        <Grid item xs={12} sm={6}>
                            <FormControl
                                fullWidth
                                size="small"
                                error={!!errors.lab}
                            >
                                <InputLabel>Select Lab *</InputLabel>
                                <Select
                                    label="Select Lab *"
                                    value={selectedLab}
                                    onChange={(e) => handleLabChange(e.target.value)}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <ScienceIcon color={selectedLab ? "success" : "primary"} />
                                        </InputAdornment>
                                    }
                                >
                                    <MenuItem value="">
                                        <em>Select a Laboratory</em>
                                    </MenuItem>
                                    {AVAILABLE_LABS.map((lab, idx) => (
                                        <MenuItem key={idx} value={lab.labId}>
                                            {lab.labName} (Account: {lab.accountNumber})
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.lab && (
                                    <span style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "3px", marginLeft: "14px" }}>
                                        {errors.lab}
                                    </span>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Orders Count */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                size="small"
                                label="Number of Orders *"
                                type="number"
                                fullWidth
                                value={ordersCount}
                                onChange={(e) => {
                                    setOrdersCount(parseInt(e.target.value) || 1);
                                    setErrors((prev) => ({ ...prev, ordersCount: "" }));
                                }}
                                inputProps={{ min: 1, max: 10000 }}
                                helperText={errors.ordersCount || "Number of orders to reserve (1-10,000)"}
                                error={!!errors.ordersCount}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Enter the number of orders you want to reserve for this lab">
                                                <InfoIcon fontSize="small" color="primary" />
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* Lab Details Display */}
                        {selectedLab && (
                            <Grid item xs={12}>
                                <Alert severity="info" icon={<CheckCircleIcon />}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Selected Lab Details
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Lab:</strong> {getSelectedLabDetails()?.labName}
                                        <br />
                                        <strong>Account Number:</strong> {getSelectedLabDetails()?.accountNumber}
                                        <br />
                                        <strong>Lab ID:</strong> {getSelectedLabDetails()?.labId}
                                        <br />
                                        <strong>Orders to Reserve:</strong> {ordersCount.toLocaleString()}
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}
                    </Grid>

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            size="large"
                            onClick={() => (window.location.href = "/vlp/new-order")}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={handleReset}
                            size="large"
                            disabled={loading}
                        >
                            Reset
                        </Button>

                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleSubmit}
                            size="large"
                            disabled={loading}
                            sx={{ minWidth: 120 }}
                        >
                            {loading ? "Processing..." : "Reserve Orders"}
                        </Button>
                    </Box>
                </Paper>

                {/* Warning for large orders */}
                {isLargeOrder && (
                    <Alert
                        severity="warning"
                        icon={<WarningIcon />}
                        sx={{ marginBottom: 3 }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Large Order Reservation ({ordersCount.toLocaleString()} orders)
                        </Typography>
                        <Typography variant="body2">
                            This will reserve a significant number of orders. Please confirm this is the correct amount before proceeding.
                        </Typography>
                    </Alert>
                )}

                {/* Information Notice */}
                <Alert
                    severity="info"
                    icon={<InfoIcon />}
                    sx={{ mt: 3, mb: 3, p: 2, borderLeft: "6px solid #0288d1" }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        ℹ️ About Order Reservation
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                        This feature allows you to reserve a specific number of orders for laboratory processing.
                        The reserved orders will be allocated to the selected laboratory account.
                    </Typography>

                    <Typography variant="body2">
                        <strong>Note:</strong> Additional laboratories can be configured in the future as needed.
                        Currently supporting: {AVAILABLE_LABS.map(lab => lab.labName).join(", ")}
                    </Typography>
                </Alert>

                <CommonLoader loading={loading} />
            </div>
        </>
    );
}